export type SearchPhase = 'initial' | 'filters' | 'listing';
export type SearchTrigger = 'initial' | 'external_form' | 'unmarked_entry' | 'empty_dates' | 'hotel_back' | 'search_form' | 'calendar' | 'repeat' | 'reload' | 'history' | 'unknown_entry' | 'filters';
export interface SearchContext {
  [key: string]: any;
  search_attempt_id: string;
  search_phase: SearchPhase;
}

/** Analytics metadata stays outside search API parameters. One instance per search surface. */
export class SearchAnalytics {
  private current: SearchContext | null = null;
  private completed = new Map<string, string>();
  private pageOpened = false;
  private datesReported = new Set<string>();
  private pendingDates = new Map<string, number>();
  private initialConsumed = false;

  constructor(private win: any, private scope: 'general' | 'hotel', private enabled: boolean) {}

  context(): SearchContext | null { return this.current; }

  openPage(params: any): void {
    if (this.pageOpened) { return; }
    this.pageOpened = true;
    this.emit('search_open_page', {
      analytics_version: 3, search_scope: this.scope,
      ...(this.scope === 'hotel' ? {hotel_id: params.hotel_id,
        expired_tour_entry: this.win.app?.pageParams?.expired_tour_entry === true} : {}),
      search_product: params.onlyHotels === true || Number(params.hotelsSearch) === 1 ? 'accommodation' : 'tour',
      from: new URL(this.win.location.href).searchParams.get('from') || 'no_from',
    });
  }

  alternativeDates(context: SearchContext, count: number): void {
    if (!context || context.search_phase !== 'initial'
        || this.current?.search_phase !== 'initial'
        || context.search_attempt_id !== this.current?.search_attempt_id
        || this.datesReported.has(context.search_attempt_id)) { return; }
    if (!this.completed.has(context.search_attempt_id)) {
      this.pendingDates.set(context.search_attempt_id, count);
      return;
    }
    if (this.completed.get(context.search_attempt_id) !== 'search_result_empty') { return; }
    this.datesReported.add(context.search_attempt_id);
    this.emit(count > 0 ? 'search_block_shown_when_search_results_empty' : 'search_result_empty_without_alternative_dates', {
      ...context, has_available_dates: count > 0, suggested_dates_count: count,
    });
  }

  begin(params: any, trigger: SearchTrigger): SearchContext {
    if (trigger === 'filters' && this.current) {
      this.current = Object.freeze({...this.current, ...params, from: this.current.from,
        search_product: this.current.search_product, hotel_id: this.current.hotel_id,
        search_phase: 'filters' as SearchPhase});
      this.win.__searchAnalyticsContext = this.current;
      return this.current;
    }
    const requestedTrigger = trigger;
    if (trigger === 'initial' && this.initialConsumed) { trigger = 'repeat'; }
    const query = new URL(this.win.location.href).searchParams;
    const from = query.get('from') || '';
    const navigation = this.win.performance?.getEntriesByType('navigation')?.[0]?.type || 'unknown';
    let parent = this.current?.search_attempt_id || '';
    if (trigger === 'initial') {
      const back = this.consumeBack();
      const source = query.get('search_source');
      if (back && navigation !== 'reload') {
        trigger = 'hotel_back';
        parent = back.parent || '';
      } else if (!this.initialConsumed && navigation === 'reload') {
        trigger = 'reload';
      } else if (!this.initialConsumed && navigation === 'back_forward') {
        trigger = 'history';
      } else if (['external_form', 'empty_dates', 'hotel_back', 'search_form', 'calendar'].includes(source)) {
        trigger = source as SearchTrigger;
        parent = query.get('search_parent') || '';
      } else {
        trigger = from || source ? 'unknown_entry' : 'unmarked_entry';
      }
      this.initialConsumed = true;
    }
    this.current = Object.freeze({
      ...params,
      analytics_version: 3,
      ...(this.scope === 'hotel' ? {expired_tour_entry: this.win.app?.pageParams?.expired_tour_entry === true} : {}),
      from: requestedTrigger === 'search_form' ? (this.scope === 'hotel' ? 'hotel-tours' : 'find-tours')
        : requestedTrigger === 'empty_dates' ? 'alternative_date'
        : this.scope === 'hotel' && requestedTrigger === 'calendar' ? 'hotel-calendar'
        : requestedTrigger !== 'initial' || this.current ? (this.current?.from || from || 'no_from')
        : from || 'no_from',
      search_scope: this.scope,
      search_product: params.onlyHotels === true || Number(params.hotelsSearch) === 1 ? 'accommodation' : 'tour',
      search_trigger: trigger,
      search_entry_from: from,
      search_navigation_type: navigation,
      search_attempt_id: this.win.crypto?.randomUUID?.() || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2),
      parent_search_attempt_id: parent,
      search_phase: 'initial' as SearchPhase,
      place: this.scope === 'hotel' ? 'hotel-tour-list' : 'clientSearch',
      mode: this.scope === 'hotel' ? 'tourList' : 'search',
    });
    this.win.__searchAnalyticsContext = this.current;
    this.emit('search_start', this.current);
    return this.current;
  }

  markFilters(): void {
    if (this.current) {
      this.current = Object.freeze({...this.current, search_phase: 'filters' as SearchPhase});
      this.win.__searchAnalyticsContext = this.current;
    }
  }

  result(event: string, context: SearchContext | null = this.current, phase?: SearchPhase, properties: Record<string, any> = {}): void {
    if (!context) { return; }
    const actualPhase = phase || context.search_phase;
    if (actualPhase === 'listing') { return; }
    if (actualPhase === 'filters') {
      this.emit(event === 'search_result_empty' ? 'search_result_empty_with_filters' : event + '_with_filters', {...context, ...properties, search_phase: 'filters'});
      return;
    }
    if (this.completed.has(context.search_attempt_id)) { return; }
    this.completed.set(context.search_attempt_id, event);
    this.emit(event, {...context, ...properties, search_phase: 'initial'});
    if (this.pendingDates.has(context.search_attempt_id)) {
      this.alternativeDates(context, this.pendingDates.get(context.search_attempt_id)!);
      this.pendingDates.delete(context.search_attempt_id);
    }
  }

  private emit(event: string, properties: Record<string, any>): void {
    if (this.enabled) { this.win.brxTracking?.sendAnalyticsEvent((this.scope === 'general' ? 'main_' : 'hotel_') + event, properties); }
  }

  private consumeBack(): any {
    try {
      const raw = this.win.sessionStorage.getItem('search_attribution_back');
      if (!raw) { return null; }
      this.win.sessionStorage.removeItem('search_attribution_back');
      const value = JSON.parse(raw);
      return value.target === this.win.location.href && Date.now() - value.created >= 0 && Date.now() - value.created < 15000 ? value : null;
    } catch (_) { return null; }
  }
}
